// Accessing localStorage/sessionStorage throws SecurityError in Firefox hardened mode.
// These wrappers re-throw anything other than SecurityError so real errors (e.g. QuotaExceededError) still surface.

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function isSecurityError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'SecurityError';
}

function createSafeStorage(getStore: () => Storage): StorageLike {
  let warned = false;

  const warn = () => {
    if (warned) return;
    warned = true;
    console.warn('[safeStorage] storage is unavailable (SecurityError); reads return null, writes are no-ops');
  };

  return {
    getItem(key: string): string | null {
      try {
        return getStore().getItem(key);
      } catch (error) {
        if (!isSecurityError(error)) throw error;
        warn();
        return null;
      }
    },
    setItem(key: string, value: string): void {
      try {
        getStore().setItem(key, value);
      } catch (error) {
        if (!isSecurityError(error)) throw error;
        warn();
      }
    },
    removeItem(key: string): void {
      try {
        getStore().removeItem(key);
      } catch (error) {
        if (!isSecurityError(error)) throw error;
        warn();
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage(() => localStorage);
export const safeSessionStorage = createSafeStorage(() => sessionStorage);
